import { useTranslation } from 'react-i18next'
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport'
import { PublicPageLayout, type PublicDestination } from './PublicPageLayout'

const PRIVACY_SECTIONS = ['identity', 'information', 'purposes', 'providers', 'rights', 'retention'] as const
const TERMS_SECTIONS = ['account', 'service', 'subscriptions', 'responsibilities', 'availability', 'termination', 'liability', 'law'] as const

interface PublicLegalPageProps {
  page: 'privacy' | 'terms'
  onNavigate: (destination: PublicDestination) => void
}

export function PublicLegalPage({ page, onNavigate }: PublicLegalPageProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  const sections = page === 'privacy' ? PRIVACY_SECTIONS : TERMS_SECTIONS

  return (
    <PublicPageLayout active={page} onNavigate={onNavigate}>
      <header style={{ borderBottom: '1px solid var(--border-subtle)', padding: isMobile ? 'var(--sp-8) var(--sp-6)' : 'var(--sp-12) min(8.33vw, 120px) var(--sp-10)' }}>
        <h1 style={{ color: 'var(--brand-900)', fontSize: isMobile ? 'var(--fs-30)' : 'var(--fs-48)', fontWeight: 'var(--fw-extra)', letterSpacing: 'var(--ls-display)', lineHeight: 'var(--lh-snug)', margin: 0 }}>{t(`public.site.${page}.title`)}</h1>
        <p style={{ color: 'var(--fg-muted)', fontSize: 'var(--fs-14)', margin: 'var(--sp-2) 0 0' }}>{t('public.site.lastUpdated')}</p>
      </header>

      <div style={{ alignItems: 'start', display: 'grid', gap: isMobile ? 'var(--sp-8)' : 'var(--sp-20)', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '240px minmax(0, 760px)', justifyContent: 'center', padding: isMobile ? 'var(--sp-8) var(--sp-6) var(--sp-16)' : 'var(--sp-10) min(8.33vw, 120px) var(--sp-20)' }}>
        {!isMobile ? (
          <nav aria-label={t('public.site.onThisPage')} style={{ borderTop: '2px solid var(--brand-500)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', position: 'sticky', top: 'var(--sp-6)', paddingBlock: 'var(--sp-5)' }}>
            <strong style={{ color: 'var(--fg-muted)', fontSize: 'var(--fs-12)' }}>{t('public.site.onThisPage')}</strong>
            {sections.map((section, index) => (
              <a key={section} href={`#${page}-${section}`} style={{ color: 'var(--fg-muted)', fontSize: 'var(--fs-14)', textDecoration: 'none' }}>
                {index + 1} · {t(`public.site.${page}.sections.${section}.title`)}
              </a>
            ))}
          </nav>
        ) : null}

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {sections.map((section, index) => (
            <section id={`${page}-${section}`} key={section} style={{ borderBottom: index === sections.length - 1 ? undefined : '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', paddingBlock: index === 0 ? `0 var(--sp-${isMobile ? '8' : '10'})` : `var(--sp-${isMobile ? '8' : '10'})` }}>
              <span style={{ color: 'var(--brand-500)', fontSize: 'var(--fs-14)', fontWeight: 'var(--fw-bold)' }}>{String(index + 1).padStart(2, '0')}</span>
              <h2 style={{ color: 'var(--brand-900)', fontSize: isMobile ? 'var(--fs-24)' : 'var(--fs-30)', fontWeight: 'var(--fw-extra)', lineHeight: 'var(--lh-snug)', margin: 0 }}>{t(`public.site.${page}.sections.${section}.title`)}</h2>
              <p style={{ color: 'var(--fg-muted)', fontSize: isMobile ? 'var(--fs-16)' : 'var(--fs-18)', lineHeight: 'var(--lh-relaxed)', margin: 0, whiteSpace: 'pre-line' }}>{t(`public.site.${page}.sections.${section}.body`)}</p>
              {page === 'privacy' && section === 'retention' ? (
                <a href="mailto:support@tlaaq.com" style={{ background: 'var(--brand-900)', borderRadius: 'var(--r-lg)', color: 'var(--white)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', marginTop: 'var(--sp-3)', padding: 'var(--sp-6)', textDecoration: 'none' }}>
                  <strong style={{ fontSize: 'var(--fs-20)' }}>{t('public.site.privacy.request.title')}</strong>
                  <span style={{ color: 'var(--brand-100)', fontSize: 'var(--fs-16)' }}>{t('public.site.privacy.request.body')}</span>
                </a>
              ) : null}
            </section>
          ))}
        </div>
      </div>
    </PublicPageLayout>
  )
}
